import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, LockOpen, Loader2 } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';

const field = 'h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600/40';

// Opened from the maintenance page once the phrase is typed; super admin credentials unlock the portal.
export function TagMaintenanceUnlockDialog({ phrase, onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || busy) return;
    setBusy(true);
    try {
      await notesApi.post('/public/portal-unlock', { phrase, email: email.trim(), password });
      try {
        const { data } = await notesApi.post('/notes/auth/login', { email: email.trim(), password });
        localStorage.setItem('notes_token', data.access_token);
        localStorage.setItem('notes_user', JSON.stringify(data.user));
      } catch { /* unlocked; the login page will ask again */ }
      toast.success('Portal unlocked — users can sign in again.');
      const p = window.location.pathname;
      window.location.replace(p.startsWith('/t/') || p.startsWith('/l/') ? window.location.href : '/');
    } catch (err) {
      toast.error(extractError(err, 'Could not unlock the portal.'));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[220] flex items-end justify-center sm:items-center sm:p-3" data-testid="tagws-unlock-dialog">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={() => !busy && onClose()} />
      <form onSubmit={submit} className="relative z-10 w-full rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] text-left shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 sm:max-w-sm sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 bg-red-600/10 px-5 pb-4 pt-5 sm:rounded-t-2xl">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-red-600 text-white"><LockOpen size={20} /></span>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Unlock portal</h3>
              <p className="text-xs text-slate-500">Super admin sign-in required</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 hover:bg-white/60" data-testid="tagws-unlock-close"><X size={18} /></button>
        </div>
        <div className="space-y-3 p-5">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Super admin email" autoComplete="username" autoFocus className={field} data-testid="tagws-unlock-email" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" className={field} data-testid="tagws-unlock-password" />
          <button type="submit" disabled={busy || !email.trim() || !password}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-red-600 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60" data-testid="tagws-unlock-submit">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <LockOpen size={15} />} Unlock portal
          </button>
        </div>
      </form>
    </div>
  );
}
